import React, { useCallback, useEffect } from "react";
import { Contact } from "src/types/contact";
import Button from "src/components/button";
import Avatar from "../avatar";
import ProfileCrop from "../crop";
import { useDispatch } from "react-redux";
import {
  fetchOrganization,
  invalidateOrganizations,
} from "src/redux/actions/organization";
import InfiniteScroll from "src/components/infiniteScroll";
import { RootState } from "src/redux/store";
import { useSelector } from "react-redux";

import _ from "lodash";
import cx from "classnames";
import styles from "./form.module.scss";
interface IContactForm {
  toggleShow?: () => void;
  contact?: Contact;
  toggleCropView?: (crop: boolean) => void;
  onSave: (contact: Contact) => void;
  invalidateUpdationStatus?: () => void;
}

//having a default prop for contact add mode so in future component can be reused
const defaultContact: Contact = {
  id: "",
  firstName: "",
  lastName: "",
  email: "",
  title: "",
  company: "",
  workPhone: "",
  mobilePhone: "",
  facebook: "",
  linkedin: "",
  twitter: "",
  instagram: "",
  icon: null,
  updating: false,
};

const ContactForm: React.FC<IContactForm> = ({
  toggleShow,
  contact = defaultContact,
  toggleCropView,
  onSave,
  invalidateUpdationStatus,
}) => {
  //states
  const [contactData, setContactData] = React.useState<Contact>(contact);
  const [toggleCrop, setToggleCrop] = React.useState(false);
  const [errors, setErrors] = React.useState<any>({});

  //side effects
  useEffect(() => {
    if (toggleCropView) {
      toggleCropView(toggleCrop);
    }
  }, [toggleCrop]);

  useEffect(() => {  
    return () => {
      if(invalidateUpdationStatus){
        invalidateUpdationStatus();
      }
    }
  }, [])
  
  useEffect(() => {
    if (contact.updating === false && toggleShow) {
      toggleShow();
    }
  }, [contact.updating]);

  //functions
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setContactData({ ...contactData, [name]: value });
  };

  const handleSave = () => {
    if (!contactData.firstName) {
      setErrors({
        ...errors,
        firstName: "Enter a valid first name",
      });
    } else if (!contactData.email) {
      setErrors({
        ...errors,
        email: "Email is required",
      });
    } else {
      let request: Contact = {
        firstName: contactData.firstName,
        lastName: contactData.lastName || "",
        email: contactData.email || "",
        title: contactData.title || "",
        company: contactData.company || "",
        workPhone: contactData.workPhone || "",
        mobilePhone: contactData.mobilePhone || "",
        facebook: contactData.facebook || "",
        twitter: contactData.twitter || "",
        linkedin: contactData.linkedin || "",
        instagram: contactData.instagram || "",
        id: contactData.id,
        icon: contactData.icon,
      };
      if (
        !_.isEqual(
          _.get(contact, "icon.thumbnail"),
          _.get(contactData, "icon.thumbnail"),
        )
      ) {
        {
          request = {
            ...request,
            icon: _.get(contactData, "icon.thumbnail"),
          };
        }
      }
      onSave(request);
    }
  };

  const changeProfileImage = (image: string) => {
    setContactData({
      ...contactData,
      icon: {
        thumbnail: image,
      },
    });
  };

  //render
  return (
    <div
      className={cx(styles.container, {
        [styles.adjustedWidth]: toggleCrop,
      })}
    >
      {!toggleCrop && (
        <>
          <Avatar
            contact={contactData}
            enableCrop={() => setToggleCrop(true)}
            changeProfileImage={changeProfileImage}
          />
          <form className={styles.form}>
            <section className={styles.inputs}>
              <span className={styles.sectionLabel}>Personal</span>
              <section className={styles.personalSection}>
                <InputGroup
                  value={contactData.firstName}
                  onChange={handleInputChange}
                  placeholder="Enter first name"
                  label="First Name"
                  name="firstName"
                  error = {errors.firstName}
                />

                <InputGroup
                  value={contactData.lastName}
                  onChange={handleInputChange}
                  placeholder="Enter last name"
                  label="Last Name"
                  name="lastName"
                />
                <InputGroup
                  value={contactData.email}
                  name="email"
                  readonly={contact.email ? true : false}
                  label="Email"
                  placeholder="Enter email"
                  onChange={handleInputChange}
                />
                {errors.email && (
                  <span className={styles.error}>{errors.email}</span>
                )}
                <InputGroup
                  value={contactData.title}
                  onChange={handleInputChange}
                  label="Title"
                  name="title"
                  placeholder="Enter Title"
                />
                <OrganizationSearch
                  onSelect={(value) => {
                    setContactData({
                      ...contactData,
                      company: value,
                    });
                  }}
                  value={contactData.company || ""}
                />
                <InputGroup
                  value={contactData.workPhone}
                  onChange={handleInputChange}
                  name="workPhone"
                  label="Work Phone"
                  placeholder="Enter work phone"
                />
                <InputGroup
                  value={contactData.mobilePhone}
                  label="Mobile"
                  onChange={handleInputChange}
                  name="mobilePhone"
                  placeholder="Enter mobile number"
                />
              </section>

              <span className={styles.sectionLabel}>Social Profiles</span>
              <section className={styles.socialSection}>
                <InputGroup
                  value={contactData.facebook}
                  placeholder="Enter facebook profile"
                  label="Facebook"
                  name="facebook"
                  onChange={handleInputChange}
                />
                <InputGroup
                  value={contactData.linkedin}
                  placeholder="Enter linkedIn ID"
                  label="LinkedIn"
                  name="linkedin"
                  onChange={handleInputChange}
                />
                <InputGroup
                  value={contactData.twitter}
                  placeholder="Enter twitter handle"
                  label="Twitter"
                  name="twitter"
                  onChange={handleInputChange}
                />
                <InputGroup
                  value={contactData.instagram}
                  placeholder="Enter Instagam ID"
                  label="Instagram"
                  name="instagram"
                  onChange={handleInputChange}
                />
              </section>
            </section>

            <div className={styles.actions}>
              <Button
                onClick={handleSave}
                disabled={contact.updating || false}
                text={contact.updating ? "Saving" : "Save"}
                variant="primary"
                icon="paperflite-tick"
              />
              <Button
                onClick={() => {
                  setContactData(contact);
                  setErrors({});
                  if (toggleShow) {
                    toggleShow();
                  }
                }}
                text="Cancel"
              />
            </div>
          </form>
        </>
      )}

      {toggleCrop && (
        <ProfileCrop
          image={_.get(contactData, "icon.thumbnail")}
          toggleCrop={() => setToggleCrop(false)}
          changeProfileImage={changeProfileImage}
        />
      )}
    </div>
  );
};

export default ContactForm;

interface IInputGroup {
  label: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  value: any;
  readonly?: boolean;
  placeholder?: string;
  name: string;
  error?: any
}
const InputGroup: React.FC<IInputGroup> = ({
  label,
  onChange,
  value,
  readonly = false,
  placeholder,
  name,
  error
}) => {
  return (
    <div className={styles.inputGroup}>
      <label>{label}</label>
      <div className={styles.inputWrapper}>
        <input
          autoComplete="off"
          placeholder={placeholder}
          className={styles.formInput}
          type="text"
          name={name}
          onChange={onChange}
          value={value}
          readOnly={readonly}
        />
        {error && (
          <span className={styles.error}>
            {" "}
            <i className="paperflite-danger" /> {error}
          </span>
        )}
      </div>
    </div>
  );
};

interface IOrganizationSearch {
  onSelect: (value: string) => void;
  value: string;
}

const OrganizationSearch: React.FC<IOrganizationSearch> = ({ onSelect, value }) => {
  const dispatch = useDispatch();
  const [query, setQuery] = React.useState<string>(value);
  const { items, pagination, loading } = useSelector(
    (state: RootState) => state.organizations,
  );
  const [showResults, setShowResults] = React.useState(false);

  const fetchQuery = useCallback(
    _.debounce((query: string) => {
      dispatch(fetchOrganization({ query, page: 0 }));
    }, 500),
    [],
  );
  return (
    <div className={cx(styles.inputGroup, styles.searchInput)}>
      <label>Company</label>

      <div className={styles.inputWrapper}>
      <input
        autoComplete="off"
        placeholder="Enter company"
        className={styles.formInput}
        type="text"
        value={query}
        onChange={(e) => {
          dispatch(invalidateOrganizations());
          setQuery(e.target.value);
          setShowResults(true);
          if (e.target.value) {
            fetchQuery(e.target.value);
          }
        }}
        name="company"
      />
      </div>
      {query && showResults && (
        <div className={styles.searchResults}>
          <InfiniteScroll
            loadMore={() => {
              dispatch(
                fetchOrganization({ query, page: pagination.currentPage + 1 }),
              );
            }}
            loading={loading}
            loader={() =>
              _.times(3, () => (
                <div className={cx(styles.placeholder)}>
                  <div className={cx(styles.block, styles.item)} />
                </div>
              ))
            }
            hasMore={pagination.currentPage < pagination.totalPages}
          >
            {_.size(items) === 0 && !loading && (
              <span
                className={styles.addNew}
                onClick={() => {
                  onSelect(query);
                  setShowResults(false);
                }}
              >
                {" "}
                <i className="paperflite-add" /> add <strong>{query}</strong> as
                a new organization
              </span>
            )}
            {_.size(items) > 0 &&
              items.map((item, index) => (
                <div
                  className={styles.result}
                  key={index}
                  onClick={() => {
                    onSelect(item.name);
                    setQuery(item.name);
                    setShowResults(false);
                  }}
                >
                  {item.name}
                </div>
              ))}
          </InfiniteScroll>
        </div>
      )}
    </div>
  );
};
